import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";

import { $requestPageLoad } from "../../internal/document-loader/registry.atom";
import { $editorState, $editorStateEffect, $editorStateError } from "../../internal/prosemirror";
import { editorStateKeyForDocsRoute } from "../../internal/prosemirror/editorStateKey";

export const useEditorState = (documentId: string, pageId: string) => {
	const editorStateKey = useMemo(
		() => editorStateKeyForDocsRoute({ documentId, pageId }),
		[documentId, pageId]
	);

	// Mount the side effects (collab sync, presence, persistence)
	useAtomValue($editorStateEffect(editorStateKey));

	// Editor state atoms - use empty string as fallback key (will return null)
	const editorState = useAtomValue($editorState(editorStateKey));
	const applyTransaction = useSetAtom($editorState(editorStateKey));
	const error = useAtomValue($editorStateError(editorStateKey));
	const requestPageLoad = useSetAtom($requestPageLoad);

	// On-demand loading: if the editor state hasn't been loaded yet (null) and there's
	// no error, request that the document loader load this specific page. This supports
	// lazy loading for large documents where not all pages are loaded eagerly.
	useEffect(() => {
		if (!editorState && !error && documentId && pageId) {
			requestPageLoad({ documentId, pageId });
		}
	}, [editorState, error, documentId, pageId, requestPageLoad]);

	return {
		/** The unique key for this editor state instance */
		editorStateKey,
		/** The current ProseMirror EditorState, or null if not loaded */
		editorState,
		/** Dispatch a transaction to update the editor state */
		applyTransaction,
		/** The editor state error, if any */
		error,
		/** True while the context or editor state is loading */
		isLoading: !editorState,
		/** True if there was an error loading the context or editor state */
		isError: !!error,
	};
};
